import type {
	ChatMessage,
	ConversationSummary,
	ConversationTransferNotification,
	MembershipSummary,
	TenantMemberProfile,
} from '@livechat/shared';
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import {
	currentAccessToken,
	listConversations,
	onAccessTokenChanged,
	realtimeUrl,
	refreshSession,
} from '../api.js';
import {
	isAssignedVisitorMessage,
	newChatToast,
	newMessageToast,
	transferredChatToast,
	type ChatToast,
} from '../chat-notifications.js';
import {
	playNotificationSound,
	showSystemNotification,
	visualNotificationMethod,
} from '../notification-alerts.js';
import { workspaceHref } from '../workspace-location.js';
import { NotificationToastRegion } from './NotificationToastRegion.js';

function mergeConversation(
	current: ConversationSummary[],
	conversation: ConversationSummary,
): ConversationSummary[] {
	return [conversation, ...current.filter((item) => item.id !== conversation.id)];
}

export function GlobalNotifications({
	membership,
	onOpenConversation,
	profile,
	suppressConversationId,
}: {
	membership: MembershipSummary;
	onOpenConversation: (conversationId: string, claimConversation?: boolean) => void;
	profile: TenantMemberProfile;
	suppressConversationId?: string;
}) {
	const [toasts, setToasts] = useState<ChatToast[]>([]);
	const conversationsRef = useRef<ConversationSummary[]>([]);
	const notificationPreferencesRef = useRef(profile);
	const onOpenConversationRef = useRef(onOpenConversation);
	const recentNewChatsRef = useRef(new Map<string, number>());
	const suppressConversationIdRef = useRef(suppressConversationId);
	const toastTimersRef = useRef(new Map<string, number>());

	useEffect(() => {
		notificationPreferencesRef.current = profile;
	}, [profile]);

	useEffect(() => {
		onOpenConversationRef.current = onOpenConversation;
	}, [onOpenConversation]);

	useEffect(() => {
		suppressConversationIdRef.current = suppressConversationId;
	}, [suppressConversationId]);

	function dismissToast(id: string): void {
		const timer = toastTimersRef.current.get(id);
		if (timer !== undefined) window.clearTimeout(timer);
		toastTimersRef.current.delete(id);
		setToasts((current) => current.filter((toast) => toast.id !== id));
	}

	function openToast(toast: ChatToast): void {
		dismissToast(toast.id);
		onOpenConversationRef.current(toast.conversationId, toast.kind === 'new-chat');
	}

	function showBrowserToast(toast: ChatToast): void {
		setToasts((current) =>
			[...current.filter((item) => item.id !== toast.id), toast].slice(-3),
		);
		const previousTimer = toastTimersRef.current.get(toast.id);
		if (previousTimer !== undefined) window.clearTimeout(previousTimer);
		toastTimersRef.current.set(
			toast.id,
			window.setTimeout(() => dismissToast(toast.id), 8_000),
		);
	}

	function notify(toast: ChatToast): void {
		const preferences = notificationPreferencesRef.current;
		const enabled =
			toast.kind === 'new-chat' || toast.kind === 'transferred-chat'
				? preferences.newChatNotificationsEnabled
				: preferences.newMessageNotificationsEnabled;
		if (!enabled) return;

		if (visualNotificationMethod(preferences.browserNotificationsEnabled) === 'system') {
			const chatUrl = new URL(
				workspaceHref({
					claimConversation: toast.kind === 'new-chat',
					conversationId: toast.conversationId,
					tab: 'chats',
				}),
				window.location.origin,
			).href;
			void showSystemNotification(toast, () => openToast(toast), chatUrl);
		} else {
			showBrowserToast(toast);
		}
		if (preferences.soundNotificationsEnabled) {
			void playNotificationSound(preferences.notificationSound);
		}
	}

	useEffect(() => {
		setToasts([]);
		conversationsRef.current = [];
		recentNewChatsRef.current.clear();
		void listConversations(membership.tenantId)
			.then((conversations) => {
				conversationsRef.current = conversations;
			})
			.catch(() => undefined);
		const token = currentAccessToken();
		if (!token) return;
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId: membership.tenantId },
			transports: ['websocket', 'polling'],
		});
		let lastRealtimeRefreshAt = 0;
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId: membership.tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					socket.auth = {
						accessToken: authentication.accessToken,
						tenantId: membership.tenantId,
					};
					socket.connect();
				})
				.catch(() => undefined);
		});
		socket.on('conversation:created', (conversation: ConversationSummary) => {
			conversationsRef.current = mergeConversation(conversationsRef.current, conversation);
			recentNewChatsRef.current.set(conversation.id, Date.now());
			notify(newChatToast(conversation));
		});
		socket.on('conversation:updated', (conversation: ConversationSummary) => {
			conversationsRef.current = mergeConversation(conversationsRef.current, conversation);
		});
		socket.on('conversation:transferred', (notification: ConversationTransferNotification) => {
			conversationsRef.current = mergeConversation(
				conversationsRef.current,
				notification.conversation,
			);
			notify(transferredChatToast(notification));
		});
		socket.on('message:created', (message: ChatMessage) => {
			const recentNewChatAt = recentNewChatsRef.current.get(message.conversationId);
			const isInitialMessage =
				recentNewChatAt !== undefined && Date.now() - recentNewChatAt < 5_000;
			if (isInitialMessage) recentNewChatsRef.current.delete(message.conversationId);
			const conversation = conversationsRef.current.find(
				(item) => item.id === message.conversationId,
			);
			if (
				!isAssignedVisitorMessage(
					message,
					conversation,
					notificationPreferencesRef.current.id,
				)
			) {
				return;
			}
			if (
				!isInitialMessage &&
				(message.conversationId !== suppressConversationIdRef.current || document.hidden)
			) {
				notify(newMessageToast(message, conversation));
			}
		});
		return () => {
			stopWatchingToken();
			for (const timer of toastTimersRef.current.values()) window.clearTimeout(timer);
			toastTimersRef.current.clear();
			socket.disconnect();
		};
	}, [membership.tenantId]);

	return <NotificationToastRegion dismiss={dismissToast} open={openToast} toasts={toasts} />;
}
